import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, CalendarDays, MessageCircle } from "lucide-react";

const services = [
  {
    n: "01",
    title: "30-Day Content Ideas",
    desc: "A month of strategic prompts mapped to your voice, audience, and offers — so you stop staring at a blank screen.",
    icon: Sparkles,
  },
  {
    n: "02",
    title: "Content Calendar Setup",
    desc: "Your ideas, organized and scheduled. We translate strategy into a plan you can actually publish — Sunday-night-friendly.",
    icon: CalendarDays,
  },
  {
    n: "03",
    title: "30-Minute Consult Call",
    desc: "Bring your scattered thoughts. Leave with a sharper hook, a clear next step, and content that finally sounds like you.",
    icon: MessageCircle,
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        className="relative pt-20 md:pt-28 pb-24 md:pb-32 px-6 md:px-12 lg:px-16"
        data-testid="home-hero"
      >
        <div className="max-w-5xl mx-auto text-center">
          <p className="overline text-[var(--gold)] rise" data-testid="home-overline">
            Legacy Content Lab · Est. for the long game
          </p>

          <h1
            className="font-serif-display mt-6 text-[2.6rem] sm:text-6xl lg:text-[5rem] leading-[1.04] tracking-tight text-[var(--indigo)] rise rise-d1"
            data-testid="home-headline"
          >
            Content strategy{" "}
            <span className="italic text-[var(--gold)]">that sounds</span>{" "}
            <br className="hidden md:block" />
            like you — not the algorithm.
          </h1>

          <p
            className="mt-7 max-w-2xl mx-auto text-[1.05rem] md:text-[1.15rem] leading-relaxed text-[var(--ink-muted)] rise rise-d2"
            data-testid="home-subheadline"
          >
            For Black & Brown women entrepreneurs ready to turn scattered ideas
            into a brand voice that books clients, builds trust, and travels
            well — from your group chat to LinkedIn.
          </p>

          <div className="mt-10 flex items-center justify-center rise rise-d3">
            <Link
              to="/book"
              className="btn-gold group"
              data-testid="home-cta-primary"
            >
              Book a Session
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
          </div>

          <div
            className="mt-10 flex flex-wrap items-center justify-center gap-2.5 rise rise-d4"
            data-testid="home-audience-tags"
          >
            {[
              "Founders & Creators",
              "Personal Brands",
              "Strategists & Coaches",
              "Faith + Family Led",
            ].map((t) => (
              <span key={t} className="tag tag-on-light" data-testid={`tag-${t.replace(/\s+/g, "-").toLowerCase()}`}>
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Decorative hairline */}
        <div className="max-w-4xl mx-auto mt-20 hairline" />
      </section>

      {/* SERVICES — three column on deep indigo */}
      <section
        className="relative bg-[var(--indigo)] text-[var(--cream)] grain overflow-hidden"
        data-testid="home-services"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-16">
            <div className="md:col-span-7">
              <p className="overline text-[var(--gold)]">Ways we can work together</p>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5">
                Built for the woman who has{" "}
                <span className="italic text-[var(--gold)]">a lot to say</span>{" "}
                — and a business to run.
              </h2>
            </div>
            <div className="md:col-span-5 md:pl-8">
              <p className="text-[var(--cream)]/75 text-[1.02rem] leading-relaxed">
                You don't need another generic content course. You need a strategy
                that respects your time, your voice, and the way your community
                actually talks. Pick the door that fits.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="service-card-dark"
                  data-testid={`service-card-${s.n}`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="num-badge">{s.n} / 03</span>
                    <Icon size={18} className="text-[var(--gold)]" />
                  </div>
                  <h3 className="font-serif-display text-[1.7rem] md:text-[1.85rem] leading-snug text-[var(--gold)]">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-[var(--cream)]/75 text-[0.98rem] leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <p className="text-[var(--cream)]/70 max-w-md text-[0.95rem]">
              Two signature offers. Both designed to take you from blank page to
              brand voice in under thirty days.
            </p>
            <Link to="/services" className="btn-ghost-dark" data-testid="home-cta-services">
              See Services & Pricing →
            </Link>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section
        className="px-6 md:px-12 lg:px-16 py-24 md:py-32"
        data-testid="home-founder"
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5 relative">
            <div className="relative">
              <div className="absolute -top-5 -left-5 w-full h-full border border-[var(--gold)]/60" aria-hidden />
              <img
                src="https://customer-assets.emergentagent.com/job_07c24cf9-f4e8-4101-b6a6-374758153700/artifacts/6260kjmj_Gemini_Generated_Image_lcghqflcghqflcgh.png"
                alt="Khalilah — CEO & AI Certified Architect at Legacy Content Lab"
                className="relative w-full h-auto object-cover"
                data-testid="founder-portrait"
              />
            </div>
          </div>
          <div className="md:col-span-7 md:pl-6">
            <p className="overline text-[var(--gold)]">Meet your strategist</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.08] tracking-tight mt-5 text-[var(--indigo)]">
              I'm Khalilah —{" "}
              <span className="italic text-[var(--gold)]">your content sister</span>{" "}
              with a strategy spreadsheet.
            </h2>
            <p className="mt-7 text-[1.05rem] leading-relaxed text-[var(--ink-muted)]">
              I help Black and Brown women entrepreneurs turn scattered ideas
              into content that actually sounds like them. No stiff frameworks.
              No "post 90 reels in 30 days" pressure. Just clear strategy,
              language that lands, and AI tools used like the assistants they
              were meant to be — quietly, in service of your voice.
            </p>
            <p className="mt-5 text-[1.05rem] leading-relaxed text-[var(--ink-muted)]">
              You'll leave our work with content you're proud of, messaging
              your audience will quote back to you, and the kind of clarity
              that lets you log off and live your life.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5">
              <p className="font-serif-display italic text-2xl text-[var(--indigo)]">
                — Khalilah
              </p>
              <span className="text-xs uppercase tracking-[0.25em] text-[var(--ink-muted)]">
                CEO · AI Certified Architect
              </span>
            </div>
            <Link to="/book" className="btn-ghost-light mt-10 inline-flex" data-testid="home-cta-founder">
              Work With Me →
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section
        className="bg-[var(--cream-2)] px-6 md:px-12 lg:px-16 py-24 md:py-32"
        data-testid="home-testimonial"
      >
        <div className="max-w-4xl mx-auto text-center">
          <p className="overline text-[var(--gold)]">What clients say</p>
          <blockquote className="font-serif-display text-[1.85rem] sm:text-3xl md:text-4xl lg:text-[2.6rem] leading-[1.25] tracking-tight text-[var(--indigo)] mt-7">
            "She made content feel{" "}
            <span className="italic text-[var(--gold)]">doable</span>. I came in
            with twelve open tabs and a Notes app full of half-thoughts. I left
            with a brand voice, a calendar, and language that sounded like the
            real me — not a LinkedIn version of me."
          </blockquote>
          <div className="mt-10 inline-flex flex-col items-center">
            <p className="font-serif-display text-lg text-[var(--indigo)]">
              A. Williams
            </p>
            <p className="overline text-[var(--ink-muted)] mt-1">
              Brand Coach · Atlanta, GA
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            {[
              { k: "Clear", v: "Strategy translated into language you can publish on a Tuesday." },
              { k: "Doable", v: "Plans that respect your time, energy, and the season you're in." },
              { k: "You", v: "Content that still sounds like the woman your friends know." },
            ].map((b) => (
              <div key={b.k} className="border-t border-[var(--border-soft)] pt-6">
                <p className="font-serif-display text-2xl text-[var(--indigo)]">{b.k}.</p>
                <p className="mt-2 text-sm text-[var(--ink-muted)] leading-relaxed">{b.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section
        className="bg-[var(--indigo)] text-[var(--cream)] px-6 md:px-12 lg:px-16 py-24 md:py-32 text-center grain relative"
        data-testid="home-cta-closing"
      >
        <div className="max-w-3xl mx-auto relative">
          <p className="overline text-[var(--gold)]">Your move</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.08] tracking-tight mt-6">
            Ready to build something{" "}
            <span className="italic text-[var(--gold)]">worth remembering?</span>
          </h2>
          <p className="mt-6 text-[var(--cream)]/75 text-[1.05rem] leading-relaxed">
            Thirty minutes. One honest conversation. A plan that finally feels
            like yours.
          </p>
          <Link to="/book" className="btn-gold mt-10 inline-flex" data-testid="home-cta-final">
            Book Your Session
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
